import React from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import Checkbox from "../ui/Checkbox";
import FieldLabel from "../ui/FieldLabel";
import MutedText from "../ui/MutedText";
import Tooltip from "../ui/Tooltip";

export const OCTO_PROPRIO_SLOTS = 8;

const EPS = 1e-9;

export function computeSelectedDriverGripperMax(jointsData, selectedQposIndices) {
  if (!jointsData || !Array.isArray(selectedQposIndices) || selectedQposIndices.length === 0) {
    return null;
  }
  const selected = new Set(selectedQposIndices.map((v) => Number(v)).filter(Number.isFinite));
  if (selected.size === 0) return null;

  const joints = Array.isArray(jointsData?.joints) ? jointsData.joints : [];
  const actuators = Array.isArray(jointsData?.actuators) ? jointsData.actuators : [];
  const jointByQpos = new Map();
  joints.forEach((joint) => {
    const idx = Number(joint?.qposIdx);
    if (Number.isFinite(idx)) jointByQpos.set(idx, joint);
  });

  const candidates = actuators.filter(
    (a) => a?.jointType === "driver" && selected.has(Number(a?.qposIdx))
  );
  if (!candidates.length) return null;

  let best = null;
  candidates.forEach((a) => {
    const idx = Number(a?.qposIdx);
    const joint = jointByQpos.get(idx);
    const lo = Number(joint?.rangeMin);
    const hi = Number(joint?.rangeMax);
    let candidate = null;
    if (Number.isFinite(lo) && Number.isFinite(hi)) {
      // Normalize by max absolute excursion so ranges like [0, -0.22] map to 0.22.
      candidate = Math.max(Math.abs(lo), Math.abs(hi));
    } else {
      const span = Number(a?.span);
      if (Number.isFinite(span)) candidate = Math.abs(span);
    }
    if (candidate != null && candidate > EPS) {
      if (best == null || candidate > best) best = candidate;
    }
  });

  return best;
}

const OctoProprioSelector = ({
  open,
  onOpenChange,
  connected,
  jointsLoading,
  onInspect,
  jointsData,
  selectedQposIndices,
  onToggleJoint,
  gripperMax,
  onGripperMaxChange,
  onAutoFillGripperMax,
  maxSlots = OCTO_PROPRIO_SLOTS,
  emptyStateText = 'Click "Inspect joints" to load joint data from the project archive.',
  emptyIndicesText = "(none)",
  noActuatorsText = "No actuators found in project archive.",
  wrapperClassName = "border border-gray-700 rounded-md bg-gray-900/30 p-3",
}) => {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className={wrapperClassName}>
        <div className="flex items-center justify-between gap-2">
          <CollapsibleTrigger className="flex-1 text-xs text-gray-200 text-left">
            Robot proprio joints
          </CollapsibleTrigger>
          <button
            className="px-2 py-0.5 rounded border border-gray-600 text-[11px] text-gray-300 hover:bg-gray-700 disabled:opacity-50 shrink-0"
            disabled={!connected || jointsLoading}
            onClick={onInspect}
          >
            {jointsLoading ? "Loading..." : "Inspect joints"}
          </button>
        </div>

        <CollapsibleContent>
          {jointsData == null ? (
            <MutedText as="p" className="mt-2 text-[11px]">
              {emptyStateText}
            </MutedText>
          ) : (
            <div className="mt-3 space-y-3">
              <div>
                <FieldLabel className="text-[11px] mb-1">
                  Selected proprio ({selectedQposIndices.length}/{maxSlots} slots)
                </FieldLabel>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: maxSlots }).map((_, slotIdx) => {
                    const qposIdx = selectedQposIndices[slotIdx];
                    const actuator =
                      qposIdx != null
                        ? (jointsData.actuators ?? []).find((a) => a.qposIdx === qposIdx)
                        : null;
                    const joint =
                      qposIdx != null && !actuator
                        ? (jointsData.joints ?? []).find((j) => j.qposIdx === qposIdx)
                        : null;
                    const item = actuator ?? joint;
                    const typeColor =
                      item?.jointType === "arm"
                        ? "bg-gray-800 border-gray-600 text-gray-200"
                        : item?.jointType === "driver"
                          ? "bg-green-900/40 border-green-600 text-green-200"
                          : "bg-gray-700 border-gray-600 text-gray-400";
                    const fullName = item ? item.displayName || item.name || "" : "";
                    const shortLabel = fullName.split(/[\s_]/)[0].slice(0, 10);
                    const label = item
                      ? `[${qposIdx}] ${shortLabel || (item.jointType === "arm" ? "arm" : "grip")}`
                      : "-";
                    return (
                      <Tooltip
                        key={slotIdx}
                        content={item ? `${fullName}\nqpos[${qposIdx}]` : "empty slot"}
                      >
                        <div
                          className={`px-1.5 py-0.5 rounded border text-[10px] whitespace-nowrap ${typeColor}`}
                        >
                          {label}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-0.5 border border-gray-800 rounded bg-gray-950/30 p-1 max-h-44 overflow-auto">
                {(jointsData.actuators ?? []).map((a) => {
                  const checked = selectedQposIndices.includes(a.qposIdx);
                  const disabled = !checked && selectedQposIndices.length >= maxSlots;
                  const badgeColor =
                    a.jointType === "arm"
                      ? "bg-gray-700 text-gray-300"
                      : a.jointType === "driver"
                        ? "bg-green-900/50 border border-green-700 text-green-300"
                        : "bg-gray-800 panel-text-helper";
                  const badgeLabel =
                    a.jointType === "arm" ? "ARM" : a.jointType === "driver" ? "GRP" : "---";
                  const friendlyName = a.displayName || a.name || "";
                  return (
                    <Checkbox
                      key={a.actId}
                      checked={checked}
                      disabled={disabled}
                      onChange={() => onToggleJoint(a.qposIdx)}
                      label={
                        <>
                          <span className={`px-1 rounded text-[10px] shrink-0 ${badgeColor}`}>
                            {badgeLabel}
                          </span>
                          <div className="flex items-center justify-between flex-1 min-w-0 gap-2">
                            <span className="text-gray-200 text-xs truncate select-none">
                              {friendlyName}
                            </span>
                            <span className="panel-text-helper text-[11px] shrink-0 select-none">
                              {a.resolvedJointName
                                ? `${a.targetName} > ${a.resolvedJointName}`
                                : a.targetName || `qpos[${a.qposIdx}]`}
                            </span>
                          </div>
                        </>
                      }
                      className={`gap-1.5 px-1.5 py-1 rounded text-[11px]
                        ${
                          checked
                            ? a.jointType === "driver"
                              ? "bg-green-900/20 border border-green-800"
                              : "bg-gray-800/60"
                            : "hover:bg-gray-800/30"
                        }
                        ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    />
                  );
                })}
                {!jointsData.actuators?.length && (
                  <MutedText className="px-1.5 py-1 text-[11px] italic">
                    {noActuatorsText}
                  </MutedText>
                )}
              </div>

              <div className="flex items-center gap-2">
                <FieldLabel as="label" className="text-[11px] text-gray-300 shrink-0">
                  Gripper max
                </FieldLabel>
                <input
                  className="w-20 px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-100 text-[11px]"
                  type="number"
                  min={0}
                  step="any"
                  value={gripperMax}
                  placeholder="e.g. 0.9"
                  onChange={(e) => onGripperMaxChange(e.target.value)}
                />
                <Tooltip
                  content={(() => {
                    const v = computeSelectedDriverGripperMax(jointsData, selectedQposIndices);
                    if (!(v != null && Number.isFinite(v))) return "Select a GRP actuator first";
                    return `From selected GRP joint range: max |q| = ${Number(v).toFixed(4)}`;
                  })()}
                >
                  <button
                    className="px-2 py-0.5 rounded border border-gray-600 text-[11px] text-gray-300 hover:bg-gray-700"
                    onClick={onAutoFillGripperMax}
                    aria-label="Auto-fill gripper max"
                  >
                    Auto-fill
                  </button>
                </Tooltip>
                <MutedText as="span" className="text-[10px] flex-1">
                  normalises selected gripper joint magnitude to [0, 1]
                </MutedText>
              </div>

              <FieldLabel className="text-[11px]">
                <MutedText as="span">indices: </MutedText>
                {selectedQposIndices.length > 0 ? (
                  selectedQposIndices.join(", ")
                ) : (
                  <span className="panel-text-disabled">{emptyIndicesText}</span>
                )}
              </FieldLabel>
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default OctoProprioSelector;
